import * as Ariakit from '@ariakit/react';
import clsx from 'clsx';
import { createContext, useCallback, useMemo } from '@wordpress/element';
import { __, _n, sprintf } from '@wordpress/i18n';
import { VisuallyHidden } from '../visually-hidden';
import styles from './style.module.scss';
import type {
	CustomSelectContext as CustomSelectContextType,
	CustomSelectStore,
	CustomSelectButtonProps,
	CustomSelectButtonSize,
	CustomSelectInternalProps,
	_CustomSelectProps,
} from './types';
import InputBase from '../input-control/input-base';
import SelectControlChevronDown from '../select-control/chevron-down';
import BaseControl from '../base-control';

export const CustomSelectContext =
	createContext< CustomSelectContextType >( undefined );
CustomSelectContext.displayName = 'CustomSelectContext';

function defaultRenderSelectedValue(
	value: CustomSelectButtonProps[ 'defaultValue' ]
) {
	const isValueEmpty = Array.isArray( value )
		? value.length === 0
		: value === undefined || value === null;

	if ( isValueEmpty ) {
		return __( 'Select an item' );
	}

	if ( Array.isArray( value ) ) {
		return value.length === 1
			? value[ 0 ]
			: sprintf(
					// translators: %d: number of items selected (it will always be 2 or more items)
					_n( '%d item selected', '%d items selected', value.length ),
					value.length
				);
	}

	return value;
}

const CustomSelectButton = ( {
	renderSelectedValue,
	size = 'default',
	store,
	className,
	...restProps
}: Omit<
	React.ComponentProps< typeof Ariakit.Select > &
		CustomSelectButtonProps &
		CustomSelectButtonSize &
		CustomSelectStore,
	'onChange'
> ) => {
	const { value: currentValue } = Ariakit.useStoreState( store );

	const computedRenderSelectedValue = useMemo(
		() => renderSelectedValue ?? defaultRenderSelectedValue,
		[ renderSelectedValue ]
	);

	return (
		<Ariakit.Select
			{ ...restProps }
			className={ clsx( styles.select, className, {
				[ styles[ 'is-compact' ] ]: size === 'compact',
				[ styles[ 'is-small' ] ]: size === 'small',
				[ styles[ 'has-custom-render' ] ]: !! renderSelectedValue,
			} ) }
			store={ store }
		>
			{ computedRenderSelectedValue( currentValue ) }
		</Ariakit.Select>
	);
};

function CustomSelect(
	props: CustomSelectInternalProps &
		_CustomSelectProps &
		CustomSelectStore &
		CustomSelectButtonSize
) {
	const {
		children,
		hideLabelFromVision = false,
		label,
		size,
		store,
		className,
		isLegacy = false,
		...restProps
	} = props;

	const onSelectPopoverKeyDown: React.KeyboardEventHandler< HTMLDivElement > =
		useCallback(
			( e ) => {
				if ( isLegacy ) {
					e.stopPropagation();
				}
			},
			[ isLegacy ]
		);

	const contextValue = useMemo( () => ( { store, size } ), [ store, size ] );

	return (
		// Where should `restProps` be forwarded to?
		<div className={ className }>
			<Ariakit.SelectLabel
				store={ store }
				render={
					hideLabelFromVision ? (
						// @ts-expect-error `children` are passed via the render prop
						<VisuallyHidden />
					) : (
						// @ts-expect-error `children` are passed via the render prop
						<BaseControl.VisualLabel as="div" />
					)
				}
			>
				{ label }
			</Ariakit.SelectLabel>
			<InputBase size={ size } suffix={ <SelectControlChevronDown /> }>
				<CustomSelectButton
					{ ...restProps }
					size={ size }
					store={ store }
					// Match legacy behavior (move selection rather than open the popover)
					showOnKeyDown={ ! isLegacy }
				/>
				<Ariakit.SelectPopover
					className={ styles.popover }
					gutter={ 12 }
					store={ store }
					sameWidth
					slide={ false }
					onKeyDown={ onSelectPopoverKeyDown }
					// Match legacy behavior
					flip={ ! isLegacy }
				>
					<CustomSelectContext.Provider value={ contextValue }>
						{ children }
					</CustomSelectContext.Provider>
				</Ariakit.SelectPopover>
			</InputBase>
		</div>
	);
}

export default CustomSelect;
